import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';

import { GridLayout } from 'src/areas/product-collection/components/products/layout-grid';
import {
  trackKeywordSearchPageOfferClick,
  trackKeywordSearchPageProductClick,
} from '../../../tracking';

import { LayoutMode } from 'src/components/layout-buttons/types';
import {
  TrackClickScope,
  trackOfferClickWithRevenue,
} from 'src/globals/tracking/conversion';
import { useKWLPWidePageExperiment } from 'src/hooks/experiments';
import {
  Offer,
  Price,
  Product,
  Raw,
  SearchResultFiltersKwlp as SearchResultFilters,
  SortInfo,
} from 'src/types';
import { productPriceClick } from '../../../../product/actions/product';

type NewFacets = SearchResultFilters | undefined;

type Props = {
  title: string;
  facets?: NewFacets;
  products: (
    | Omit<Product, 'classicFullPath' | 'conditions' | 'media'>
    | Omit<Raw, 'store'>
    | Omit<
        Offer,
        | 'stockStatus'
        | 'id'
        | 'store'
        | 'media'
        | 'name'
        | 'externalUri'
        | 'offerPrice'
      >
    | null
  )[];
  sort?: SortInfo | null;
  isWide?: boolean;
};

const ADS_LAYOUT = {
  [LayoutMode.GRID]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: false, row: 2 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.COMPACT_LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 15 },
  },
};

export const ProductResults = (props: Props) => {
  const { products, isWide, title } = props;

  const dispatch = useDispatch();
  // hardcoded value to keep logic here for next a/b test with different design
  const smallerProductCardsMobileExperiment = false;
  const isExtraWide = useKWLPWidePageExperiment();

  useEffect(() => {
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: `keywordlandingpage:layout:${LayoutMode.GRID}`,
    });
  }, []);

  const productIds = useMemo(
    () =>
      products?.length
        ? (products
            .map(product => (product && 'id' in product ? product.id : null))
            .filter(id => id) as number[])
        : [],
    [products]
  );
  const productsWithActiveCampaign = useActiveCampaignByProductId(
    productIds,
    true
  );

  const onProductClickHandler = (
    id: string | number,
    _name?: string,
    type?: string
  ) => {
    if (type === 'Offer') {
      const clickedProduct = products.find(p => {
        return p?.__typename === 'Offer' && (p as Offer)?.id === id;
      }) as Offer | undefined;
      const { offerId } = clickedProduct || {};

      // Dummy Price object need here for the clickout event (only the type is needed)
      const clickoutPrice: Price = {
        type: '',
        authorizedDealer: false,
      };
      dispatch(
        productPriceClick(
          null,
          clickoutPrice,
          !!clickedProduct?.store.featured,
          'raw'
        )
      );
      if (offerId) {
        trackKeywordSearchPageOfferClick(title, offerId);
        trackOfferClickWithRevenue({
          scope: TrackClickScope.KEYWORD_PAGE,
          offerId,
        });
      }
    }
    if (type === 'Product') trackKeywordSearchPageProductClick(title, id);
  };

  return (
    <GridLayout
      productsWithActiveCampaign={productsWithActiveCampaign}
      isExtraWide={isExtraWide}
      products={
        products.filter(
          p =>
            p?.__typename === 'Product' ||
            p?.__typename === 'Raw' ||
            p?.__typename === 'Offer'
        ) as (Product | Raw)[]
      }
      adslayoutConfig={ADS_LAYOUT}
      onClick={onProductClickHandler}
      isWide={isWide}
      smallerProductCardsMobileExperiment={smallerProductCardsMobileExperiment}
    />
  );
};
